import { cn } from "@bindery/ui/lib/utils";
import { lazy, Suspense } from "react";
import { useLocation } from "react-router";
import { useDesign, useDesignPalette } from "@/components/design-switcher";
import { BrandLogo, SmallCaps } from "./brand";
import { DesignSwitcher } from "./design-switcher-menu";
import { MobileMenu } from "./mobile-menu";
import { NavLinks } from "./nav-links";
import { UserMenu } from "./user-menu";

// Reading ids are lowercase UUIDs; /reader/<id>/affiliate keeps the global bar.
const READING_PAGE_PATTERN = /^\/reader\/[a-f0-9-]{36}$/;

export function isReadingPage(pathname: string): boolean {
  return READING_PAGE_PATTERN.test(pathname);
}

export function Navbar() {
  const { pathname } = useLocation();
  return isReadingPage(pathname) ? (
    <Suspense fallback={null}>
      <ReadingChrome />
    </Suspense>
  ) : (
    <GlobalNavbar />
  );
}

// Lazy with the reading page: both read the reader's fluency and UI stores.
const ReadingChrome = lazy(() =>
  import("./reading-chrome").then((m) => ({ default: m.ReadingChrome })),
);

export const CAPSULE_CLASS =
  "flex items-center rounded-2xl border px-3 py-1.5 shadow-[0_8px_32px_-8px_rgba(0,0,0,0.45)] backdrop-blur-2xl";
const DARK_GLASS_CLASS = "border-white/[0.12] bg-black/60";

// ../app pinned LANDING_EXPERIMENT to "combo-c": the variation's palette
// paints the capsule on the landing and on reading pages, nowhere else.
export function GlobalNavbar() {
  const { activeSlug, setActiveSlug } = useDesign();
  const palette = useDesignPalette();
  const { pathname } = useLocation();
  const usePalette = pathname === "/";
  const brandColor = usePalette ? { color: palette.text } : undefined;

  return (
    <header className="fixed top-4 left-1/2 z-[60] -translate-x-1/2">
      <nav
        aria-label="Global"
        className={cn(CAPSULE_CLASS, "gap-1", !usePalette && DARK_GLASS_CLASS)}
        style={
          usePalette
            ? {
                backgroundColor: palette.surface,
                borderColor: palette.border,
                color: palette.text,
              }
            : undefined
        }
      >
        <div className="flex items-center gap-3 md:hidden">
          <SmallCaps>
            <span
              className={cn(
                "text-sm font-medium tracking-wide",
                !usePalette && "text-white/80",
              )}
              style={brandColor}
            >
              Read by Ear
            </span>
          </SmallCaps>
          <MobileMenu triggerColor={usePalette ? palette.text : undefined} />
        </div>

        <div className="hidden items-center justify-between gap-3 md:flex">
          <BrandLogo large />
          <div className="flex flex-col items-center gap-2">
            <SmallCaps>
              <span
                className={cn(
                  "text-lg font-medium tracking-wide",
                  !usePalette && "text-white/80",
                )}
                style={brandColor}
              >
                Read by Ear
              </span>
            </SmallCaps>
            <NavLinks />
          </div>
          <div className="flex items-center gap-0.5">
            <div
              className={cn("mx-2 h-5 w-px", !usePalette && "bg-white/[0.08]")}
              style={
                usePalette ? { backgroundColor: palette.border } : undefined
              }
              aria-hidden="true"
            />
            <div className="flex items-center gap-0.5">
              <DesignSwitcher
                activeSlug={activeSlug}
                onSelect={setActiveSlug}
                palette={usePalette ? palette : undefined}
              />
              <UserMenu palette={usePalette ? palette : undefined} />
            </div>
          </div>
        </div>
      </nav>
    </header>
  );
}
